/**
 * TierShowcase — swipeable full-design cards for every tier (Rewards Hub).
 * Opens on the customer's current tier; locked tiers keep full colour and
 * show a lock badge with the three unlock paths.
 */
import { useEffect, useRef, useState } from 'react';
import { Lock, Check, Sparkles } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { TIER_ORDER, tierLook, TierCardFrame, TierCardHeader, PerkText } from './CustomerTierCard';
import { useTierPerks } from '../hooks/useTierPerks';

function unlockText(t) {
  const parts = [`${t.ordersThreshold} orders`, `${t.pointsThreshold.toLocaleString()} pts`];
  if (t.referralsThreshold > 0) parts.push(`${t.referralsThreshold} referrals`);
  return `Unlock with ${parts.join(' · or ')}`;
}

function StatusBadge({ status, look, tierKey }) {
  if (status === 'current') {
    return (
      <div className="am-tier-chip" style={{ borderColor: `${look.tc}55`, background: `${look.tc}12`, color: look.tc }} data-testid={`showcase-current-${tierKey}`}>
        <Sparkles className="am-tier-chip-icon" /><span>Your current tier</span>
      </div>
    );
  }
  if (status === 'earned') {
    return (
      <div className="am-tier-chip" style={{ borderColor: `${look.tc}40`, color: look.tc }} data-testid={`showcase-earned-${tierKey}`}>
        <Check className="am-tier-chip-icon" /><span>Unlocked</span>
      </div>
    );
  }
  return (
    <div className="am-showcase-lock" data-testid={`showcase-lock-${tierKey}`}>
      <span className="am-showcase-lock-badge" style={{ color: look.tc }}><Lock className="w-3.5 h-3.5" /></span>
      <span>{unlockText(look.t)}</span>
    </div>
  );
}

export function ShowcaseSlide({ tierKey, status, isModern }) {
  const look = tierLook(tierKey, isModern, useTierPerks());
  return (
    <div className="am-showcase-slide" data-testid={`showcase-slide-${tierKey}`} data-status={status}>
      <TierCardFrame look={look} isModern={isModern} testid={`showcase-card-${tierKey}`} className="am-showcase-card">
        <TierCardHeader look={look} isModern={isModern}
          eyebrow={status === 'current' ? 'YOUR TIER' : `${look.t.label} TIER`}
          sub={status === 'current' ? look.t.headline : status === 'earned' ? 'Already unlocked' : 'Not unlocked yet'} />
        <StatusBadge status={status} look={look} tierKey={tierKey} />
        <div className="am-tier-perks">
          <div className="am-tier-perks-heading">{status === 'locked' ? 'PERKS YOU’LL UNLOCK' : 'PERKS'}</div>
          <ul>
            {look.t.perks.map((p, i) => (
              <li key={i} data-testid={`showcase-perk-${tierKey}-${i}`}>
                <span className="am-tier-perk-mark" style={{ color: look.tc }}>▸</span>
                <PerkText perk={p} />
              </li>
            ))}
          </ul>
        </div>
      </TierCardFrame>
    </div>
  );
}

function Dots({ count, active, onPick, keys }) {
  return (
    <div className="am-showcase-dots" role="tablist" data-testid="showcase-dots">
      {Array.from({ length: count }).map((_, i) => (
        <button key={i} type="button" role="tab" aria-selected={i === active} aria-label={`Show ${keys[i]} tier`}
          className={`am-showcase-dot ${i === active ? 'is-active' : ''}`} onClick={() => onPick(i)} data-testid={`showcase-dot-${keys[i]}`} />
      ))}
    </div>
  );
}

export function TierShowcase({ currentTier = 'standard' }) {
  const { isModern } = useTheme();
  const tiers = currentTier === 'black_card' ? [...TIER_ORDER, 'black_card'] : TIER_ORDER;
  const curIdx = tiers.indexOf(currentTier);
  const [active, setActive] = useState(Math.max(0, curIdx));
  const trackRef = useRef(null);

  const scrollTo = (i, smooth = true) => {
    const el = trackRef.current?.children[i];
    if (el) trackRef.current.scrollTo({ left: el.offsetLeft - trackRef.current.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  };

  useEffect(() => { scrollTo(Math.max(0, curIdx), false); }, [curIdx]);

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const w = track.children[0]?.offsetWidth || 1;
    setActive(Math.round(track.scrollLeft / w));
  };

  const statusOf = (i) => (i === curIdx ? 'current' : i < curIdx ? 'earned' : 'locked');

  return (
    <section className="am-showcase" data-testid="tier-showcase" aria-label="All membership tiers">
      <div className="am-showcase-head">
        <h3 className={isModern ? 'am-section-title' : 'font-mono text-[#39FF14] text-sm font-bold'} data-testid="tier-showcase-title">
          {isModern ? 'All tiers' : '// ALL_TIERS'}
        </h3>
        <span className="am-showcase-hint">Swipe to explore</span>
      </div>
      <div className="am-showcase-track" ref={trackRef} onScroll={onScroll} data-testid="showcase-track">
        {tiers.map((k, i) => <ShowcaseSlide key={k} tierKey={k} status={statusOf(i)} isModern={isModern} />)}
      </div>
      <Dots count={tiers.length} active={active} keys={tiers} onPick={(i) => { setActive(i); scrollTo(i); }} />
    </section>
  );
}

export default TierShowcase;
